import type {
    ContactFieldLabelKey,
    ContactFieldLabels,
    HiddenContactFields,
    TemplateFormLanguage,
} from '../types/templateForms';
import {
    DUPLICATE_CONTACT_EMAIL_ERROR,
    DUPLICATE_CONTACT_EMAIL_SNACKBAR,
} from '../utils/contactEmails';
import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from './constants';

export interface ContactInfoTranslations {
    yourContactInformation: string;
    contactInformationOrdinal: (ordinal: string) => string;
    labels: {
        firstIdType: string;
        secondIdType: string;
        firstIdFront: string;
        firstIdBack: string;
        firstIdSingle: string;
        secondIdFront: string;
        secondIdBack: string;
        secondIdSingle: string;
        firstName: string;
        middleName: string;
        lastName: string;
        email: string;
        cellPhone: string;
        homePhone: string;
        homeAddress: string;
        unitNumber: string;
        dateOfBirth: string;
        maritalStatus: string;
        citizenshipStatus: string;
        gender: string;
        preferredPronouns: string;
        occupation: string;
        employer: string;
        employerAddress: string;
        employerPhone: string;
    };
    placeholders: {
        selectIdType: string;
        selectMaritalStatus: string;
        selectCitizenshipStatus: string;
        selectGender: string;
        preferredPronouns: string;
    };
    uploadDescriptions: {
        front: string;
        back: string;
        single: string;
    };
    idTypes: Record<string, string>;
    maritalStatuses: Record<string, string>;
    citizenshipStatuses: Record<string, string>;
    genders: Record<string, string>;
    validation: {
        fieldRequired: string;
        invalidEmail: string;
        duplicateEmail: string;
        duplicateEmailSnackbar: string;
        fillRequiredFields: string;
        invalidEmailSnackbar: string;
        contactBlockHasErrors: string;
    };
    contactBlock: {
        addEntry: (entryLabel: string) => string;
        removeEntry: (entryTitle: string) => string;
        none: string;
        maxEntriesReached: (maxEntries: number) => string;
        tooManyEntries: (maxEntries: number) => string;
        previewHintRequired: (maxEntries?: number) => string;
        previewHintOptional: (maxEntries?: number) => string;
    };
}

const ENGLISH_ID_TYPE_VALUES = [
    "Canadian driver's license",
    "Foreign driver's license",
    'Canadian passport',
    'Foreign passport',
    'Birth certificate',
    'Permanent Resident card',
] as const;

const ENGLISH_MARITAL_STATUS_VALUES = [
    'Single',
    'Married',
    'Divorced',
    'Widowed',
    'Separated',
] as const;

const ENGLISH_CITIZENSHIP_STATUS_VALUES = [
    'Canadian citizen',
    'Dual/Multiple citizenships',
    'Permanent resident of Canada',
    'Temporary resident of Canada',
    'Non-resident of Canada',
    'U.S. person',
] as const;

const ENGLISH_GENDER_VALUES = ['Male', 'Female', 'Non-binary', 'Prefer not to say'] as const;

const ENGLISH_TRANSLATIONS: ContactInfoTranslations = {
    yourContactInformation: 'Your Contact Information',
    contactInformationOrdinal: (ordinal) => `${ordinal} Contact's Information`,
    labels: {
        firstIdType: 'First ID Type',
        secondIdType: 'Second ID Type',
        firstIdFront: 'First ID - Front',
        firstIdBack: 'First ID - Back',
        firstIdSingle: 'First ID Upload',
        secondIdFront: 'Second ID - Front',
        secondIdBack: 'Second ID - Back',
        secondIdSingle: 'Second ID Upload',
        firstName: 'First Name',
        middleName: 'Middle Name',
        lastName: 'Last Name',
        email: 'Email Address',
        cellPhone: 'Cell Phone Number',
        homePhone: 'Home Phone Number',
        homeAddress: 'Home Address',
        unitNumber: 'Unit Number',
        dateOfBirth: 'Date of Birth',
        maritalStatus: 'Marital Status',
        citizenshipStatus: 'Citizenship Status',
        gender: 'Gender',
        preferredPronouns: 'Preferred Pronouns',
        occupation: 'Occupation',
        employer: 'Employer (Company Name)',
        employerAddress: 'Employer Address',
        employerPhone: 'Employer Phone Number',
    },
    placeholders: {
        selectIdType: 'Select ID type',
        selectMaritalStatus: 'Select marital status',
        selectCitizenshipStatus: 'Select citizenship status',
        selectGender: 'Select gender',
        preferredPronouns: 'e.g. she/her, he/him, they/them',
    },
    uploadDescriptions: {
        front: 'Upload the front of your ID',
        back: 'Upload the back of your ID',
        single: 'Upload an image of your ID',
    },
    idTypes: {
        "Canadian driver's license": "Canadian driver's license",
        "Foreign driver's license": "Foreign driver's license",
        'Canadian passport': 'Canadian passport',
        'Foreign passport': 'Foreign passport',
        'Birth certificate': 'Birth certificate',
        'Permanent Resident card': 'Permanent Resident card',
    },
    maritalStatuses: {
        Single: 'Single',
        Married: 'Married',
        Divorced: 'Divorced',
        Widowed: 'Widowed',
        Separated: 'Separated',
    },
    citizenshipStatuses: {
        'Canadian citizen': 'Canadian citizen',
        'Dual/Multiple citizenships': 'Canadian citizen (dual/multiple citizenships)',
        'Permanent resident of Canada': 'Permanent resident of Canada',
        'Temporary resident of Canada': 'Temporary resident (work/study permit)',
        'Non-resident of Canada': 'Non-resident of Canada',
        'U.S. person': 'U.S. citizen or person (for tax purposes)',
    },
    genders: {
        Male: 'Male',
        Female: 'Female',
        'Non-binary': 'Non-binary',
        'Prefer not to say': 'Prefer not to say',
    },
    validation: {
        fieldRequired: 'This field is required',
        invalidEmail: 'Invalid email address',
        // Shared constants: error-clearing logic compares against these exact strings
        duplicateEmail: DUPLICATE_CONTACT_EMAIL_ERROR,
        duplicateEmailSnackbar: DUPLICATE_CONTACT_EMAIL_SNACKBAR,
        fillRequiredFields: 'Please fill all required fields.',
        invalidEmailSnackbar:
            'Oops! That email format doesn’t seem right. Double-check and try again.',
        contactBlockHasErrors: 'Please complete the highlighted fields above.',
    },
    contactBlock: {
        addEntry: (entryLabel) => `Add ${entryLabel}`,
        removeEntry: (entryTitle) => `Remove ${entryTitle}`,
        none: 'None',
        maxEntriesReached: (maxEntries) => `Maximum reached (${maxEntries}).`,
        tooManyEntries: (maxEntries) => `Only ${maxEntries} allowed. Remove the extra entries.`,
        previewHintRequired: (maxEntries) => {
            if (maxEntries === 1) return 'Clients must add one entry.';
            return maxEntries
                ? `Clients must add at least one entry, up to ${maxEntries}.`
                : 'Clients must add at least one entry and can add more.';
        },
        previewHintOptional: (maxEntries) => {
            if (maxEntries === 1)
                return 'Optional. Clients can fill in one entry or leave it blank.';
            return maxEntries
                ? `Optional. Clients can fill in up to ${maxEntries} entries or leave it blank.`
                : 'Optional. Clients can fill in as many entries as needed or leave it blank.';
        },
    },
};

const FRENCH_TRANSLATIONS: ContactInfoTranslations = {
    yourContactInformation: 'Vos coordonnées',
    contactInformationOrdinal: (ordinal) => `Coordonnées du ${ordinal} contact`,
    labels: {
        firstIdType: 'Premier type de pièce d’identité',
        secondIdType: 'Deuxième type de pièce d’identité',
        firstIdFront: 'Première pièce d’identité – Recto',
        firstIdBack: 'Première pièce d’identité – Verso',
        firstIdSingle: 'Téléversement de la première pièce d’identité',
        secondIdFront: 'Deuxième pièce d’identité – Recto',
        secondIdBack: 'Deuxième pièce d’identité – Verso',
        secondIdSingle: 'Téléversement de la deuxième pièce d’identité',
        firstName: 'Prénom',
        middleName: 'Second prénom',
        lastName: 'Nom de famille',
        email: 'Adresse courriel',
        cellPhone: 'Numéro de téléphone cellulaire',
        homePhone: 'Numéro de téléphone au domicile',
        homeAddress: 'Adresse du domicile',
        // Shared by home and employer unit fields — keep neutral (no “domicile”)
        unitNumber: 'Numéro d’unité',
        dateOfBirth: 'Date de naissance',
        maritalStatus: 'État civil',
        citizenshipStatus: 'Statut de citoyenneté',
        gender: 'Genre',
        preferredPronouns: 'Pronoms préférés',
        occupation: 'Profession',
        employer: 'Employeur (nom de l’entreprise)',
        employerAddress: 'Adresse de l’employeur',
        employerPhone: 'Numéro de téléphone de l’employeur',
    },
    placeholders: {
        selectIdType: 'Sélectionnez le type de pièce d’identité',
        selectMaritalStatus: 'Sélectionnez l’état civil',
        selectCitizenshipStatus: 'Sélectionnez le statut de citoyenneté',
        selectGender: 'Sélectionnez le genre',
        preferredPronouns: 'p. ex. elle/la, il/lui, iel/iel',
    },
    uploadDescriptions: {
        front: 'Téléversez le recto de votre pièce d’identité',
        back: 'Téléversez le verso de votre pièce d’identité',
        single: 'Téléversez une image de votre pièce d’identité',
    },
    idTypes: {
        "Canadian driver's license": 'Permis de conduire canadien',
        "Foreign driver's license": 'Permis de conduire étranger',
        'Canadian passport': 'Passeport canadien',
        'Foreign passport': 'Passeport étranger',
        'Birth certificate': 'Certificat de naissance',
        'Permanent Resident card': 'Carte de résident permanent',
    },
    maritalStatuses: {
        Single: 'Célibataire',
        Married: 'Marié(e)',
        Divorced: 'Divorcé(e)',
        Widowed: 'Veuf/Veuve',
        Separated: 'Séparé(e)',
    },
    citizenshipStatuses: {
        'Canadian citizen': 'Citoyen(ne) canadien(ne)',
        'Dual/Multiple citizenships': 'Citoyen(ne) canadien(ne) (double/multiples citoyennetés)',
        'Permanent resident of Canada': 'Résident(e) permanent(e) du Canada',
        'Temporary resident of Canada': 'Résident(e) temporaire (permis de travail/d’études)',
        'Non-resident of Canada': 'Non-résident(e) du Canada',
        'U.S. person': 'Citoyen(ne) ou personne des États-Unis (aux fins fiscales)',
    },
    genders: {
        Male: 'Homme',
        Female: 'Femme',
        'Non-binary': 'Non-binaire',
        'Prefer not to say': 'Préfère ne pas répondre',
    },
    validation: {
        fieldRequired: 'Ce champ est obligatoire',
        invalidEmail: 'Adresse courriel invalide',
        duplicateEmail: 'Tous les courriels des contacts doivent être uniques',
        duplicateEmailSnackbar: 'Les adresses courriel doivent être uniques.',
        fillRequiredFields: 'Veuillez remplir tous les champs obligatoires.',
        invalidEmailSnackbar:
            'Oups! Le format de ce courriel ne semble pas correct. Vérifiez-le et réessayez.',
        contactBlockHasErrors: 'Veuillez remplir les champs en surbrillance ci-dessus.',
    },
    contactBlock: {
        addEntry: (entryLabel) => `Ajouter : ${entryLabel}`,
        removeEntry: (entryTitle) => `Retirer : ${entryTitle}`,
        none: 'Aucun',
        maxEntriesReached: (maxEntries) => `Maximum atteint (${maxEntries}).`,
        tooManyEntries: (maxEntries) => `${maxEntries} maximum. Retirez les entrées en trop.`,
        previewHintRequired: (maxEntries) => {
            if (maxEntries === 1) return 'Les clients doivent ajouter une entrée.';
            return maxEntries
                ? `Les clients doivent ajouter au moins une entrée, jusqu’à ${maxEntries}.`
                : 'Les clients doivent ajouter au moins une entrée et peuvent en ajouter d’autres.';
        },
        previewHintOptional: (maxEntries) => {
            if (maxEntries === 1)
                return 'Facultatif. Les clients peuvent remplir une entrée ou la laisser vide.';
            return maxEntries
                ? `Facultatif. Les clients peuvent remplir jusqu’à ${maxEntries} entrées ou laisser le tout vide.`
                : 'Facultatif. Les clients peuvent remplir autant d’entrées que nécessaire ou laisser le tout vide.';
        },
    },
};

export const CONTACT_INFO_TRANSLATIONS: Record<TemplateFormLanguage, ContactInfoTranslations> = {
    en: ENGLISH_TRANSLATIONS,
    fr: FRENCH_TRANSLATIONS,
};

export function getContactInfoTranslations(
    language: TemplateFormLanguage = DEFAULT_TEMPLATE_FORM_LANGUAGE,
): ContactInfoTranslations {
    return CONTACT_INFO_TRANSLATIONS[language] ?? CONTACT_INFO_TRANSLATIONS.en;
}

function getEnglishOrdinal(n: number): string {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const remainder = n % 100;
    const suffix = suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0];
    return `${n}${suffix}`;
}

function getFrenchOrdinal(n: number): string {
    if (n === 1) return '1er';
    return `${n}e`;
}

export function getContactSectionTitle(
    index: number,
    totalContacts: number,
    language: TemplateFormLanguage = DEFAULT_TEMPLATE_FORM_LANGUAGE,
): string {
    const translations = getContactInfoTranslations(language);

    if (totalContacts === 1) {
        return translations.yourContactInformation;
    }

    const ordinal = language === 'fr' ? getFrenchOrdinal(index + 1) : getEnglishOrdinal(index + 1);
    return translations.contactInformationOrdinal(ordinal);
}

export function buildSelectOptions(
    canonicalValues: readonly string[],
    labelMap: Record<string, string>,
    placeholder: string,
): { value: string; label: string }[] {
    return [
        { value: '', label: placeholder },
        ...canonicalValues.map((value) => ({
            value,
            label: labelMap[value] ?? value,
        })),
    ];
}

export const ID_TYPE_CANONICAL_VALUES = ENGLISH_ID_TYPE_VALUES;
export const MARITAL_STATUS_CANONICAL_VALUES = ENGLISH_MARITAL_STATUS_VALUES;
export const CITIZENSHIP_STATUS_CANONICAL_VALUES = ENGLISH_CITIZENSHIP_STATUS_VALUES;
export const GENDER_CANONICAL_VALUES = ENGLISH_GENDER_VALUES;

export function getDefaultContactFieldLabel(
    field: ContactFieldLabelKey,
    translations: ContactInfoTranslations,
): string {
    if (field === 'homeUnit' || field === 'employerUnit') {
        return translations.labels.unitNumber;
    }
    return translations.labels[field];
}

export function getContactFieldLabel(
    field: ContactFieldLabelKey,
    translations: ContactInfoTranslations,
    customLabels?: ContactFieldLabels,
): string {
    return customLabels?.[field]?.trim() || getDefaultContactFieldLabel(field, translations);
}

export const HIDDEN_CONTACT_FIELD_TO_LABEL_KEY: Record<
    keyof HiddenContactFields,
    ContactFieldLabelKey
> = {
    firstId: 'firstIdType',
    secondId: 'secondIdType',
    middleName: 'middleName',
    homePhone: 'homePhone',
    homeAddress: 'homeAddress',
    homeUnit: 'homeUnit',
    dateOfBirth: 'dateOfBirth',
    maritalStatus: 'maritalStatus',
    citizenshipStatus: 'citizenshipStatus',
    gender: 'gender',
    preferredPronouns: 'preferredPronouns',
    occupation: 'occupation',
    employer: 'employer',
    employerAddress: 'employerAddress',
    employerUnit: 'employerUnit',
    employerPhone: 'employerPhone',
};

const BUILDER_CONTACT_FIELD_LABEL_HINTS: Partial<Record<ContactFieldLabelKey, string>> = {
    homeUnit: 'Home',
    employerUnit: 'Employer',
};

const BUILDER_OPTIONAL_GROUP_LABELS: Partial<Record<keyof HiddenContactFields, string>> = {
    firstId: 'First ID',
    secondId: 'Second ID',
};

export function getBuilderDefaultContactFieldLabel(
    field: ContactFieldLabelKey,
    translations: ContactInfoTranslations,
): string {
    const defaultLabel = getDefaultContactFieldLabel(field, translations);
    const hint = BUILDER_CONTACT_FIELD_LABEL_HINTS[field];
    return hint ? `${defaultLabel} (${hint})` : defaultLabel;
}

export function getBuilderOptionalContactFieldLabel(
    field: keyof HiddenContactFields,
    translations: ContactInfoTranslations,
    customLabels?: ContactFieldLabels,
): string {
    const labelKey = HIDDEN_CONTACT_FIELD_TO_LABEL_KEY[field];
    const customLabel = customLabels?.[labelKey]?.trim();
    if (customLabel) {
        return customLabel;
    }
    return (
        BUILDER_OPTIONAL_GROUP_LABELS[field] ??
        getBuilderDefaultContactFieldLabel(labelKey, translations)
    );
}
